/**
 * Просмотр фото и видео во весь экран с перелистыванием: стрелки по бокам, ← → на клавиатуре,
 * свайп пальцем, лента миниатюр снизу, подпись — что за заметка и кто приложил. Esc или клик
 * мимо — закрыть. Соседние фото грузятся заранее, листание без ожидания.
 *
 * Сейчас — медиа комментариев CRM: заметки в ленте «Чатов» (`CommentBubble`) и задачи товара во
 * вкладке «Договор» (`ProductTaskList`). Спека `docs/specs/2026-10-22-chat-comment-media-design.md`.
 * Фото товара «Договора» (`SerialPhotos`) — с группами («На модели» / «На вешалке»: переключатель
 * сверху, разрыв в ленте миниатюр) и своими кнопками в шапке (`actions`) — спека
 * `docs/specs/2026-10-22-contract-photos-design.md`.
 */
import { ChevronLeft, ChevronRight, ExternalLink, Play, X } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type MediaItem = {
  kind: "photo" | "video" | "audio";
  url: string;
  /** Уменьшенная копия для миниатюры; нет — берём сам файл (у видео — значок). */
  preview?: string | null;
  /** Подпись под файлом: текст заметки. */
  caption?: string | null;
  /** Вторая строка подписи: кто, когда, к чему. */
  meta?: string | null;
  /** Группа файла («На модели»): файлы одной группы идут подряд, сверху — переход к группе. */
  group?: string | null;
};

/** Миниатюра файла: фото — превью, видео — кадр-заглушка со значком, голосовое — значок. */
export function MediaThumb({ item, size = 56, onClick }: { item: MediaItem; size?: number; onClick: () => void }) {
  const box = { width: size, height: size };
  return (
    <button
      type="button"
      onClick={onClick}
      title={item.kind === "video" ? "Открыть видео" : item.kind === "audio" ? "Голосовое" : "Открыть фото"}
      className="relative flex-none overflow-hidden rounded-md bg-black/10 outline-offset-2 hover:opacity-90 focus-visible:outline-2"
      style={box}
    >
      {item.kind === "photo" ? (
        <img src={item.preview || item.url} alt="" loading="lazy" className="size-full object-cover" />
      ) : (
        <span className="grid size-full place-items-center bg-black/60 text-white">
          <Play size={size > 40 ? 20 : 14} />
        </span>
      )}
    </button>
  );
}

export function MediaViewer({
  items,
  start,
  onClose,
  actions,
}: {
  items: MediaItem[];
  start: number;
  onClose: () => void;
  /** Свои кнопки в шапке — к открытому файлу. */
  actions?: (item: MediaItem, index: number) => ReactNode;
}) {
  const [i, setI] = useState(Math.min(Math.max(start, 0), items.length - 1));
  const many = items.length > 1;
  const go = useCallback((d: number) => setI((x) => (x + d + items.length) % items.length), [items.length]);
  const strip = useRef<HTMLDivElement>(null);
  const touch = useRef<number | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
      else return;
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [go, onClose]);

  // Текущая миниатюра — в зоне видимости ленты снизу.
  useEffect(() => {
    strip.current?.children[i]?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [i]);

  const item = items[i];
  if (!item) return null;
  // Группы — по порядку появления: первый файл и сколько их.
  const groups: { name: string; first: number; count: number }[] = [];
  items.forEach((m, k) => {
    if (!m.group) return;
    const g = groups.find((x) => x.name === m.group);
    if (g) g.count += 1;
    else groups.push({ name: m.group, first: k, count: 1 });
  });
  const near = many ? [items[(i + 1) % items.length], items[(i - 1 + items.length) % items.length]] : [];

  const arrow = "absolute top-1/2 z-10 grid size-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white hover:bg-white/25";
  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-black/95 text-white"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Просмотр файлов"
      data-annot="просмотр медиа комментариев"
    >
      <div className="flex flex-none items-center gap-3 px-4 pt-3 text-[13px]" onClick={(e) => e.stopPropagation()}>
        {many && (
          <span className="tabular-nums text-white/70">
            {i + 1} / {items.length}
          </span>
        )}
        {groups.length > 0 && (
          <span className="flex flex-wrap gap-1.5">
            {groups.map((g) => (
              <button
                key={g.name}
                type="button"
                onClick={() => setI(g.first)}
                className={`rounded-full px-3 py-1 text-[13px] ${g.name === item.group ? "bg-white text-black" : "bg-white/10 hover:bg-white/20"}`}
              >
                {g.name} <span className="tabular-nums opacity-60">{g.count}</span>
              </button>
            ))}
          </span>
        )}
        <span className="ml-auto flex flex-wrap items-center justify-end gap-2">
          {actions?.(item, i)}
          <a
            href={item.url}
            target="_blank"
            rel="noreferrer"
            title="Открыть оригинал в новой вкладке"
            className="grid size-10 place-items-center rounded-full bg-white/10 hover:bg-white/20"
          >
            <ExternalLink size={18} />
          </a>
          <button type="button" onClick={onClose} title="Закрыть (Esc)" className="grid size-10 place-items-center rounded-full bg-white/10 hover:bg-white/20">
            <X size={20} />
          </button>
        </span>
      </div>

      <div
        className="relative flex min-h-0 flex-1 items-center justify-center px-2 py-2 sm:px-16"
        onTouchStart={(e) => (touch.current = e.touches[0]?.clientX ?? null)}
        onTouchEnd={(e) => {
          const x0 = touch.current;
          const x1 = e.changedTouches[0]?.clientX;
          touch.current = null;
          if (x0 != null && x1 != null && Math.abs(x1 - x0) > 50) go(x1 < x0 ? 1 : -1);
        }}
      >
        {many && (
          <button type="button" title="Предыдущее (←)" className={`${arrow} left-3`} onClick={(e) => (e.stopPropagation(), go(-1))}>
            <ChevronLeft size={26} />
          </button>
        )}
        {item.kind === "photo" ? (
          <img key={item.url} src={item.url} alt={item.caption ?? ""} className="max-h-full max-w-full select-none object-contain" onClick={(e) => e.stopPropagation()} />
        ) : item.kind === "video" ? (
          <video key={item.url} src={item.url} controls autoPlay playsInline className="max-h-full max-w-full" onClick={(e) => e.stopPropagation()} />
        ) : (
          <audio key={item.url} src={item.url} controls autoPlay onClick={(e) => e.stopPropagation()} />
        )}
        {many && (
          <button type="button" title="Следующее (→)" className={`${arrow} right-3`} onClick={(e) => (e.stopPropagation(), go(1))}>
            <ChevronRight size={26} />
          </button>
        )}
        {/* Соседние фото — в кэш браузера заранее. */}
        {near.map((n) => n?.kind === "photo" && <link key={n.url} rel="preload" as="image" href={n.url} />)}
      </div>

      {(item.caption || item.meta) && (
        <div className="mx-auto w-full max-w-[720px] flex-none px-4 pb-2 text-center" onClick={(e) => e.stopPropagation()}>
          {item.caption && <div className="line-clamp-3 whitespace-pre-wrap text-[14px]">{item.caption}</div>}
          {item.meta && <div className="text-[12px] text-white/60">{item.meta}</div>}
        </div>
      )}

      {many && (
        <div ref={strip} className="flex flex-none justify-center-safe gap-1.5 overflow-x-auto px-4 pb-4 pt-1" onClick={(e) => e.stopPropagation()}>
          {items.map((m, k) => (
            <span
              key={`${m.url}-${k}`}
              className={`flex-none rounded-md ${k === i ? "ring-2 ring-white" : "opacity-60 hover:opacity-100"} ${k > 0 && m.group && m.group !== items[k - 1]?.group ? "ml-5" : ""}`}
            >
              <MediaThumb item={m} size={52} onClick={() => setI(k)} />
            </span>
          ))}
        </div>
      )}
    </div>,
    document.body,
  );
}
